import { Button } from "@progress/kendo-react-buttons";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import { ComboBox, ComboBoxChangeEvent, ComboBoxFilterChangeEvent, DropDownList, DropDownListChangeEvent } from "@progress/kendo-react-dropdowns";
import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { defaultDate } from "../Common/Constants";
import { updateInterruptData, addToInterruptData, getInterruptsSelector, updateSelectedSeries } from "./machineHealthSlice";
import Format from "../Common/Util_methods/formatParamOptions";
import { useAppDispatch } from "../Core/store.hooks";
import { getDevicesSelector } from "../Devices/Slice";
import { filterBy } from "@progress/kendo-data-query";
import { includes, initial, isEqual, map, uniq } from "lodash";
import { getOptionsSelector, updateMachineHealthDrillDown,updateMachineHealthGroupSize } from "../Common/Slices/optionSlice";

interface FilterProps {
    id:string;
    seriesFromGrid:Series;
}

export interface Series{
    devicesWithSerial: string[];
    deviceTypes:number[];
    deviceName:string;
    deviceNameVersion:string;
}

export interface MachineHealthParams{
    start:string;
    end:string;
    bucketSize:"y" | "M" | "w" | "d";
    seriesObjects:Series[];
}

const initialSeries:Series = {
    devicesWithSerial:[],
    deviceTypes:[],
    deviceName:null,
    deviceNameVersion:null
}

const Filters = (props: FilterProps) => {

    let startDate = new Date();
    startDate.setDate(startDate.getDate()-7);

    const [start, setStart] = useState(startDate);
    const [end, setEnd] = useState(new Date);
    const [drillDown, updateDrillDown] = useState("Off");
    const [groupSize, updateGroupSize] = useState("None")

    const seriesTypes: string[] = [
        "Customer Device",
        "Device Name",
        "Device Name + Version",
        "Device Name + Device Size + Version",
    ];
    const [seriesType, updateSeriesType] = useState("Device Name");
    const [devices, updateDevices] = useState([]);
    const [countries, updateCountries] = useState([]);
    const [customers, updateCustomers] = useState([]);
    const [country, updateCountry] = useState(null);
    const [customer, updateCustomer] = useState(null);
    const [deviceValue, updateDeviceValue] = useState(null);

    const deviceObjects = useSelector(getDevicesSelector);
    const selectedSeries = useSelector(getInterruptsSelector).selectedSeries
    const options = useSelector(getOptionsSelector)
    const dispatch = useAppDispatch();

    useEffect(()=>{
        if(deviceObjects.length > 0){
            updateDropDowns();
            paramsHandler();
        }
    },[deviceObjects])
        
    useEffect(()=>{
        if(props.seriesFromGrid != null){
            if(!selectedSeries.some((obj)=>isEqual(obj,props.seriesFromGrid))){
                dispatch(updateSelectedSeries([...selectedSeries,props.seriesFromGrid]))
                let params = {} as MachineHealthParams
                params.start = formatDate(start);
                params.end = formatDate(end);
                params.seriesObjects = [props.seriesFromGrid];
                dispatch(addToInterruptData(params))
            }else{
                alert("Series is already selected")
            }
        }
    },[props.seriesFromGrid])

    useEffect(()=>{
        if(options.machineHealthDrillDown){
            updateDrillDown("On")
        }else{
            updateDrillDown("Off")
        }
    },[options.machineHealthDrillDown])

    const seriesTypeHandler = function (e: DropDownListChangeEvent) {
        updateSeriesType(e.target.value);
        e.target.value == "Customer Device"
            ? updateDevices([])
            : updateDevices(Format.DeviceTypes(deviceObjects, e.target.value));
    };

    const updateDropDowns = function () {
        updateCountries(Format.Countries(deviceObjects));
        updateCustomers(Format.Customers(deviceObjects, null));
        updateDevices(Format.DeviceTypes(deviceObjects, "Device Name"));
    };

    const paramsHandler = () => {
        
        let params = {} as MachineHealthParams
        params.start = formatDate(start);
        params.end = formatDate(end);
        if(selectedSeries.length === 0){
            params.seriesObjects = [initialSeries]
            dispatch(updateSelectedSeries([...selectedSeries,initialSeries]))
        }else{
            params.seriesObjects = selectedSeries;
        }
        dispatch(updateInterruptData(params))
    };

    const seriesHandler = () =>{
        let newSeries:Series = {
            devicesWithSerial:[],
            deviceTypes:[],
            deviceName:null,
            deviceNameVersion:null
        }
        if (seriesType === "Customer Device"){
            newSeries.devicesWithSerial = [deviceValue]
        }
        if(seriesType === "Device Name"){
            newSeries.deviceName = deviceValue;
        }
        if(seriesType === "Device Name + Version"){
            newSeries.deviceNameVersion = deviceValue;
        }
        if(seriesType === "Device Name + Device Size + Version"){
            newSeries.deviceTypes = Format.DeviceIDsByDeviceType(deviceObjects,[deviceValue]);
        }
        if(!selectedSeries.some((obj)=>isEqual(obj,newSeries))){
            dispatch(updateSelectedSeries([...selectedSeries,newSeries]))
            let params = {} as MachineHealthParams
            params.start = formatDate(start);
            params.end = formatDate(end); 
            params.seriesObjects = [newSeries];
            dispatch(addToInterruptData(params))
        }else{
            alert("Series is already selected")
        }
    }

    const deviceHandler = function (e: ComboBoxChangeEvent) {
        
        updateDeviceValue(e.target.value);
    };

    const customerHandler = function (e: ComboBoxChangeEvent) {
        updateCustomer(e.target.value);
        updateDeviceValue(null);
        if(e.target.value == null && seriesType === "Customer Device"){
            updateDevices([])
        }else{
            let filteredDevices = Format.DevicesWithSerial(deviceObjects, e.target.value);
            updateDevices(filteredDevices);
            if (filteredDevices.length == 1) {
                updateDeviceValue(filteredDevices[0]);
            }
        }
    };
    const countryHandler = function (e: ComboBoxChangeEvent) {
        updateCountry(e.target.value);
        updateCustomer(null);
        updateDeviceValue(null);
        updateCustomers(Format.Customers(deviceObjects, e.target.value));
        updateDevices([]);
    };

    const drillDownHandler = (e:any) =>{
        //updateDrillDown(e.target.value)
        if(e.target.value==="On"){
            dispatch(updateMachineHealthDrillDown(true))
        }else{
            dispatch(updateMachineHealthDrillDown(false))
        }
    }

    const groupSizeHandler = (e:any)=>{
        updateGroupSize(e.target.value)
        dispatch(updateMachineHealthGroupSize(e.target.value))
    }

    const formatDate = (d: any) => {
        var year = d.getFullYear();
        var month = d.getMonth() + 1;
        var day = d.getDate();
        if (month < 10) month = "0" + month;
        if (day < 10) day = "0" + day;
        return year + "-" + month + "-" + day;
    };

    const comboBoxFilters = () => {
        return {
            countries: (event: ComboBoxFilterChangeEvent) => {
                updateCountries(
                    filterBy(Format.Countries(deviceObjects), event.filter)
                );
            },
            customers: (event: ComboBoxFilterChangeEvent) => {
                updateCustomers(
                    filterBy(
                        Format.Customers(deviceObjects, country),
                        event.filter
                    )
                );
            },
            devices: (event: ComboBoxFilterChangeEvent) => {
                if (seriesType === "Customer Device") {
                    updateDevices(
                        filterBy(
                            Format.DevicesWithSerial(deviceObjects, customer),
                            event.filter
                        )
                    );
                }
                else {
                    updateDevices(
                        filterBy(Format.DeviceTypes(deviceObjects, seriesType),
                            event.filter));
                }
                
            },
        };
    };
    
    const renderDateInputs = () => {
        return (
            <Fragment>
                {" "}
                <div className="k-textbox-container metricCombo">
                    <label className="k-label">Start</label>
                    <DatePicker
                        value={start}
                        onChange={(e) =>
                            e.target.value && setStart(e.target.value)
                        }
                        format={"dd.MM.yyyy"}
                    />
                </div>
                <div className="k-textbox-container metricCombo">
                    <label className="k-label">End</label>
                    <DatePicker
                        value={end}
                        onChange={(e) =>
                            e.target.value && setEnd(e.target.value)
                        }
                        format={"dd.MM.yyyy"}
                    />
                </div>
            </Fragment>
        );
    };
    ///////////////////////////////////////////////////////////////////////////////////////////////////
    return (
        <Fragment>
            <div>
                <div className="pp-sidebar-header"> Parameters </div>
                {renderDateInputs()}
                <div className=" k-textbox-container metricComboSmall">
                        <label className="k-label">GroupBy</label>
                        {/* <span
                            className="k-icon k-i-help"
                            onClick={() => toggleDialogHandler()}
                            style={{
                                cursor: "pointer",
                                position: "absolute",
                                top: "5px",
                                left: "60px",
                                fontSize: "11px",
                                marginTop: "-1px",
                                display: "inline-block",
                                verticalAlign: "top",
                            }}
                        ></span> */}
                        <DropDownList
                            data={["None","Day","Week","Month", "Year"]}
                            value={options.machineHealthGroupSize}
                            onChange={groupSizeHandler}
                        />
                    </div>
                    <div className=" k-textbox-container metricComboSmall">
                        <label className="k-label">Drill Down</label>
                        <DropDownList
                            data={["On", "Off"]}
                            value={drillDown}
                            onChange={drillDownHandler}
                        />
                    </div>
                <div>
                    <Button
                        className="pp-filter-button"
                        onClick={() => paramsHandler()}
                    >
                        UPDATE
                    </Button>
                </div>
            </div>
            <div className="pp-sidebar-header"> Add series </div>
            <div className=" k-textbox-container metricCombo">
                <label className="k-label">Series Type</label>
                <DropDownList
                    popupSettings={{width:"auto"}}
                    data={seriesTypes}
                    value={seriesType}
                    onChange={seriesTypeHandler}
                />
            </div>

            {seriesType == "Customer Device" ? (
                <Fragment>
                    <div className=" k-textbox-container metricCombo">
                        <label className="k-label">Country</label>
                        <ComboBox
                            data={countries}
                            value={country}
                            onChange={countryHandler}
                            suggest={true}
                            filterable={true}
                            onFilterChange={comboBoxFilters().countries}
                        />
                    </div>
                    <div className=" k-textbox-container metricCombo">
                        <label className="k-label">Customer</label>
                        <ComboBox
                            data={customers}
                            value={customer}
                            onChange={customerHandler}
                            suggest={true}
                            filterable={true}
                            onFilterChange={comboBoxFilters().customers}
                        />
                    </div>
                </Fragment>
            ) : null}

            <div className=" k-textbox-container metricCombo">
                <label className="k-label" style={{ width: "300px" }}>
                    {seriesType}
                </label>
                <ComboBox
                    popupSettings={{width:"auto"}}
                    data={devices}
                    value={deviceValue}
                    onChange={deviceHandler}
                    suggest={true}
                    filterable={true}
                    onFilterChange={comboBoxFilters().devices}
                
                />
            </div>
            <Button
                className="pp-filter-button"
                onClick={seriesHandler}>
                Add Series
            </Button>
        </Fragment>
    );
};

export default Filters;
